// File: src/pages/PrivacyPolicyPage.jsx

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  Page,
  Layout,
  TextContainer,
  Text,
  List as PolarisList,
  Link,
} from '@shopify/polaris';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // NEW FUNCTION: Implements "Go Back or Go Home" logic
  const handleBack = () => {
    // Check if it's likely the first route loaded in the session (key 'default')
    // or if the history is very short. If so, default to homepage ('/').
    if (location.key === 'default' || window.history.length <= 2) {
      navigate('/', { replace: true });
    } else {
      // Otherwise, safely go back one step in history
      navigate(-1);
    }
  };

  return (
    <Page
    title="BaoBrain Privacy Policy"
    backAction={{
    content: 'Back',
    onAction: handleBack,
      }}
    >
      <Layout>
        <Layout.Section>
          <TextContainer>
            <Text variant="headingMd" as="h2" marginBlockStart="4">
              Table of Contents
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item><Link url="#overview">Overview</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#controller-processor">Data Controller vs Processor</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#what-we-collect">What We Collect</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#what-we-dont-collect">What We DO NOT Collect</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#how-we-use">How We Use Data</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#ip-geo">IP Addresses & Geolocation</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#retention">Data Retention</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#security">Security Measures</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#gdpr">GDPR & CCPA Rights</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#cookies">Cookies</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#google">Google OAuth & Analytics</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#sharing">Data Sharing</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#children">Children's Privacy</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#international">International Transfers</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#changes">Changes to This Policy</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#contact">Contact</Link></PolarisList.Item>
            </PolarisList>

            <Text variant="bodyMd" as="p" fontWeight="semibold" marginBlockStart="6">
              Last Updated: December 3, 2025
            </Text>

            {/* OVERVIEW */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="overview">
              Overview
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain ("we," "us," "our") provides privacy-first behavioral analytics for e-commerce stores. This Privacy Policy explains what data we collect, how we use it, and your rights.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">Key Point:</Text> BaoBrain does NOT collect or store personal information (names, emails, phone numbers, addresses) about your store's customers. We only collect anonymous behavioral data.
            </Text>

            {/* DATA CONTROLLER VS PROCESSOR */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="controller-processor">
              Data Controller vs Data Processor
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">For Merchants Using BaoBrain:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">YOU</Text> (the merchant) are the <Text as="span" fontWeight="semibold">Data Controller</Text>. You own and control all data about your customers and store operations.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">BaoBrain</Text> is the <Text as="span" fontWeight="semibold">Data Processor</Text>. We process anonymous analytics data on your behalf to provide insights.
              </PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">Your Responsibilities as Data Controller:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Maintain your own privacy policy disclosing use of analytics tools</PolarisList.Item>
              <PolarisList.Item>Obtain necessary consents from your customers where required by law</PolarisList.Item>
              <PolarisList.Item>Comply with GDPR, CCPA, and other applicable privacy laws</PolarisList.Item>
              <PolarisList.Item>Inform customers about cookie usage and tracking</PolarisList.Item>
            </PolarisList>

            {/* WHAT WE COLLECT */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="what-we-collect">
              What Data We Collect
            </Text>

            <Text variant="headingLg" as="h3" marginBlockStart="6">
              1. Merchant Account Information
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              When you create a BaoBrain account:
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Email address</PolarisList.Item>
              <PolarisList.Item>Name (optional)</PolarisList.Item>
              <PolarisList.Item>Hashed password (we never store plaintext passwords)</PolarisList.Item>
              <PolarisList.Item>Store URL and platform type (Shopify, WooCommerce, etc.)</PolarisList.Item>
              <PolarisList.Item>Stripe customer ID for billing (we never see your credit card number)</PolarisList.Item>
              <PolarisList.Item>Subscription status and billing events</PolarisList.Item>
              <PolarisList.Item>Login attempts and security lockout information (for fraud prevention)</PolarisList.Item>
            </PolarisList>

            <Text variant="headingLg" as="h3" marginBlockStart="6">
              2. Behavioral Analytics Data (Anonymous)
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              When visitors browse stores that have installed BaoBrain, we collect <Text as="span" fontWeight="semibold">completely anonymous behavioral data</Text>:
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Session Data:</Text> Random session ID (not tied to any person), timestamp
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Behavioral Events:</Text> Page views, clicks (element selectors only), scroll depth, time on page
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Navigation:</Text> URLs visited, referrer (where they came from), UTM parameters
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Device Information:</Text> Browser type, operating system, screen dimensions, viewport size, device type (mobile/desktop)
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Performance Metrics:</Text> Page load time, browser performance data
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">E-commerce Events:</Text> Product IDs viewed, cart events, order IDs. Order IDs are numeric identifiers only and contain no associated customer information.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Location:</Text> Country, region, and city (derived from IP address - see IP section below)
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Heatmap Data:</Text> Click coordinates on page (for visual heatmaps)
              </PolarisList.Item>
            </PolarisList>

            <Text variant="headingLg" as="h3" marginBlockStart="6">
              3. Store & Platform Data
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Store URL and domain name</PolarisList.Item>
              <PolarisList.Item>Platform type (Shopify, WooCommerce, BigCommerce, custom)</PolarisList.Item>
              <PolarisList.Item>Platform access tokens (encrypted and stored securely, required to connect your store)</PolarisList.Item>
              <PolarisList.Item>Installation timestamps and configuration settings</PolarisList.Item>
            </PolarisList>

            <Text variant="headingLg" as="h3" marginBlockStart="6">
              4. A/B Testing Data
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Test configurations you create</PolarisList.Item>
              <PolarisList.Item>Variant assignments (which test version a session saw)</PolarisList.Item>
              <PolarisList.Item>Goal event tracking (conversions per variant)</PolarisList.Item>
            </PolarisList>

            <Text variant="headingLg" as="h3" marginBlockStart="6">
              5. AI-Generated Insights
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              We generate AI summaries and insights based on aggregated anonymous behavioral data. These summaries contain no personal information.
            </Text>

            <Text variant="headingLg" as="h3" marginBlockStart="6">
              6. Google Analytics Integration (Optional)
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              If you connect Google Analytics:
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>OAuth access and refresh tokens (encrypted)</PolarisList.Item>
              <PolarisList.Item>GA4 property IDs and configurations</PolarisList.Item>
              <PolarisList.Item>Google Analytics metrics (displayed in BaoBrain dashboard)</PolarisList.Item>
            </PolarisList>

            {/* WHAT WE DONT COLLECT */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="what-we-dont-collect">
              What We DO NOT Collect
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain is built for privacy. We explicitly <Text as="span" fontWeight="semibold">DO NOT</Text> collect:
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Personal Identifiable Information (PII):</Text> Names, email addresses, phone numbers, physical addresses, social security numbers, government IDs
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Payment Information:</Text> Credit card numbers, CVV codes, billing addresses, payment account details
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Login Credentials:</Text> Usernames, passwords, authentication tokens (except our own OAuth tokens for integrations)
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Biometric Data:</Text> Fingerprints, facial recognition, voice prints
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Form Input Values:</Text> Text entered into forms (contact forms, checkout fields, search boxes) — only anonymous events like "form_submitted" are tracked
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Full IP Addresses:</Text> We immediately hash/anonymize IP addresses; we never store raw IP addresses
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Precise Geolocation:</Text> GPS coordinates, street-level addresses — we only derive country/region/city from IP
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Chat/Messaging Content:</Text> Private messages, customer support conversations, chatbot transcripts
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Social Media Profiles:</Text> Facebook profiles, Instagram handles, Twitter usernames
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Health Information:</Text> Medical records, prescriptions, health conditions
              </PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">Important:</Text> If you attempt to send PII through custom events or misconfigured tracking, BaoBrain's systems are designed to detect and strip out personal data.If a merchant misconfigures BaoBrain or sends PII through a custom event, BaoBrain automatically removes or anonymizes this data and does not store it.
            </Text>

            {/* HOW WE USE DATA */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="how-we-use">
              How We Use Your Data
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">For Merchant Accounts:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Provide and operate the Services</PolarisList.Item>
              <PolarisList.Item>Process payments and manage subscriptions</PolarisList.Item>
              <PolarisList.Item>Send transactional emails (login alerts, billing receipts, service updates)</PolarisList.Item>
              <PolarisList.Item>Provide customer support</PolarisList.Item>
              <PolarisList.Item>Improve and optimize our Services</PolarisList.Item>
              <PolarisList.Item>Prevent fraud and enforce our Terms of Service</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">For Analytics Data:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Generate behavioral insights, heatmaps, and session replays for merchants</PolarisList.Item>
              <PolarisList.Item>Calculate conversion rates and identify drop-off points in funnels</PolarisList.Item>
              <PolarisList.Item>Create AI-generated recommendations and insights</PolarisList.Item>
              <PolarisList.Item>Improve our analytics algorithms and features (using aggregated, anonymized data)</PolarisList.Item>
              <PolarisList.Item>Provide industry benchmarks (anonymized and aggregated across merchants)</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">We do NOT use data for:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Selling to advertisers or data brokers</PolarisList.Item>
              <PolarisList.Item>Building cross-site tracking profiles of individual customers</PolarisList.Item>
              <PolarisList.Item>Marketing or targeting visitors on other websites</PolarisList.Item>
              <PolarisList.Item>Sharing with other merchants or third parties (except as required by law)</PolarisList.Item>
            </PolarisList>

            {/* IP ADDRESSES */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="ip-geo">
              IP Addresses & Geolocation
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">What We Do with IPs:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Collection:</Text> When a visitor loads a page with BaoBrain tracking, their IP address is temporarily received by our servers (this is unavoidable in web requests).
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Processing:</Text> We immediately use the IP to derive approximate geolocation (country, region, city) using a local IP-to-location database.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Hashing:</Text> We then hash the IP address using a one-way cryptographic function combined with a rotating secret key. This creates an anonymized identifier that cannot be reversed back to the original IP.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Storage:</Text> Only the hashed IP and derived location (country/region/city) are stored. The original IP address is immediately discarded and never stored in our database.
              </PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">Why We Hash IPs:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Prevent session duplication (a visitor refreshing the page multiple times is counted as one session)</PolarisList.Item>
              <PolarisList.Item>Detect bot traffic and filter out spam/abuse</PolarisList.Item>
              <PolarisList.Item>Provide approximate geolocation insights (e.g., "30% of visitors from California")</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">What We Don't Do:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>We do NOT store raw IP addresses</PolarisList.Item>
              <PolarisList.Item>We do NOT build dossiers or profiles tied to specific IPs</PolarisList.Item>
              <PolarisList.Item>We do NOT share IP addresses (raw or hashed) with third parties</PolarisList.Item>
              <PolarisList.Item>We do NOT use IPs for tracking across different websites</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              Under GDPR, hashed IP addresses with rotating keys and immediate discarding are considered pseudonymized data (not personal data) when used solely for analytics purposes. Server logs may contain IP addresses for security, rate limiting, and anti-abuse purposes (standard for all web infrastructure). These logs are short-lived, access-restricted, and never used for analytics or user tracking.
            </Text>

            {/* RETENTION */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="retention">
              Data Retention
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">Merchant Account Data:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Retained for as long as your Account is active</PolarisList.Item>
              <PolarisList.Item>After Account deletion: Billing records kept for 7 years (tax/legal requirements), other data deleted within 90 days</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">Analytics Data (Anonymous Behavioral Data):</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Raw event data: Retained for 13 months</PolarisList.Item>
              <PolarisList.Item>Aggregated reports: Retained indefinitely (no PII, fully anonymized)</PolarisList.Item>
              <PolarisList.Item>Session replays: Retained for 90 days</PolarisList.Item>
              <PolarisList.Item>Heatmaps: Retained for 6 months</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">Why These Periods?</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>13 months allows year-over-year comparison (e.g., "December 2024 vs December 2023")</PolarisList.Item>
              <PolarisList.Item>Aggregated data helps improve our algorithms and provide benchmarks</PolarisList.Item>
              <PolarisList.Item>Shorter retention for replays/heatmaps balances utility with privacy</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              You can request earlier deletion of your data by contacting <Link url="mailto:privacy@baobrain.com">privacy@baobrain.com</Link>.
            </Text>

            {/* SECURITY */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="security">
              Security Measures
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              We implement industry-standard security measures to protect your data:
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Encryption:</Text> Data in transit uses TLS 1.3+; data at rest encrypted using AES-256
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Password Security:</Text> Passwords hashed using bcrypt with high cost factor; plaintext passwords never stored
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">OAuth Tokens:</Text> Encrypted at rest; never logged or exposed in logs/errors
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">IP Hashing:</Text> One-way cryptographic hashing with rotating secret keys
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Access Controls:</Text> Role-based access; employee access limited to necessary operations; audit logging
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Infrastructure:</Text> Hosted on secure cloud providers (database with encrypted backups)
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Monitoring:</Text> Automated intrusion detection, rate limiting, DDoS protection
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Authentication:</Text> Rate limiting on login attempts, account lockout after failed attempts, optional 2FA (coming soon)
              </PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              Despite these measures, no system is 100% secure. If you believe your Account has been compromised, contact <Link url="mailto:security@baobrain.com">security@baobrain.com</Link> immediately.
            </Text>

            {/* GDPR & CCPA */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="gdpr">
              Your Privacy Rights (GDPR & CCPA)
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">For Merchants (Your Rights):</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Right to Access:</Text> Request a copy of all data we have about your Account
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Right to Rectification:</Text> Correct inaccurate Account information
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Right to Deletion:</Text> Request deletion of your Account and associated data (subject to legal retention requirements)
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Right to Data Portability:</Text> Export your Analytics Data in machine-readable format
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Right to Object:</Text> Object to processing of your data for certain purposes
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Right to Restrict Processing:</Text> Request limitation on how we process your data
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Right to Withdraw Consent:</Text> Cancel your subscription and stop data processing at any time
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Right to Lodge a Complaint:</Text> File a complaint with your local data protection authority
              </PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              To exercise any of these rights, contact <Link url="mailto:privacy@baobrain.com">privacy@baobrain.com</Link>. We will respond within 30 days.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">For Your Store's Customers (End Users):</Text>
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain does NOT collect personal data about end users. However, as the Data Controller, YOU (the merchant) are responsible for:
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Handling GDPR/CCPA data subject requests from your customers</PolarisList.Item>
              <PolarisList.Item>Providing opt-out mechanisms for tracking (e.g., honoring "Do Not Track" signals, cookie consent banners)</PolarisList.Item>
              <PolarisList.Item>Disclosing in your privacy policy that you use analytics tools like BaoBrain</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              If you receive a customer request to delete or export analytics data, contact us at <Link url="mailto:privacy@baobrain.com">privacy@baobrain.com</Link> and we will assist. However, since our data is anonymized and not tied to specific individuals, we typically cannot identify which data belongs to a specific customer.
            </Text>

            {/* COOKIES */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="cookies">
              Cookies and Tracking Technologies
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain uses cookies and similar technologies to provide the Services.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">On Your Store (Visitor Tracking):</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Session Cookie (_bb_session):</Text> Stores anonymous session ID, expires after 30 minutes of inactivity
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Visitor ID Cookie (_bb_visitor):</Text> Stores anonymous visitor identifier for returning visitor detection, expires after 2 years
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">A/B Test Cookie (_bb_variant):</Text> Remembers which test variant was shown, expires when test ends or after 30 days
              </PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">On BaoBrain Dashboard (Merchant Login):</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Authentication Cookie (baobrain_auth):</Text> Keeps you logged in, expires after 30 days or when you log out
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Session Cookie (baobrain_session):</Text> Manages your dashboard session state
              </PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">Third-Party Cookies:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Stripe (payment processing) - only when accessing billing pages</PolarisList.Item>
              <PolarisList.Item>Google OAuth (if you connect Google Analytics)</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              You can disable cookies in your browser settings. This may affect functionality for merchants but will prevent analytics tracking for store visitors.
            </Text>

            {/* GOOGLE */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="google">
              Google OAuth & Analytics Integration
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              When you connect Google Analytics (optional):
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>We request read-only access to your Google Analytics account</PolarisList.Item>
              <PolarisList.Item>Access and refresh tokens are encrypted and stored securely</PolarisList.Item>
              <PolarisList.Item>Data is used solely to display your GA4 metrics in BaoBrain's dashboard</PolarisList.Item>
              <PolarisList.Item>We never share your Google data with third parties</PolarisList.Item>
              <PolarisList.Item>We do NOT use Google Workspace data to train AI/ML models</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              You can revoke BaoBrain's access at any time through your <Link url="https://myaccount.google.com/permissions" external>Google Account permissions</Link> or by disconnecting in BaoBrain settings.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain's use of information received from Google APIs adheres to the <Link url="https://developers.google.com/terms/api-services-user-data-policy" external>Google API Services User Data Policy</Link>, including the Limited Use requirements.
            </Text>

            {/* SHARING */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="sharing">
              Data Sharing and Disclosure
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              We share data only in these limited circumstances:
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Service Providers:</Text> Cloud hosting (database storage), Stripe (payment processing), email services (transactional emails only). These providers are contractually obligated to protect your data and use it only for providing services to BaoBrain.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Legal Requirements:</Text> If required by law, court order, or government request
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Business Transfers:</Text> In the event of a merger, acquisition, or sale of assets (with notification to affected users)
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Protection of Rights:</Text> To protect our rights, safety, or property, or that of our users
              </PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">We do NOT:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Sell your data to advertisers, data brokers, or marketing companies</PolarisList.Item>
              <PolarisList.Item>Share analytics data between different merchants</PolarisList.Item>
              <PolarisList.Item>Provide customer data to any third party for their own use</PolarisList.Item>
            </PolarisList>

            {/* CHILDREN */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="children">
              Children's Privacy
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain is not intended for children under 13. We do not knowingly collect personal information from children. If you are under 13, do not use BaoBrain or provide any information to us.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              If we learn we have collected information from a child under 13, we will delete it immediately. Parents or guardians who believe we may have information about a child should contact <Link url="mailto:privacy@baobrain.com">privacy@baobrain.com</Link>.
            </Text>

            {/* INTERNATIONAL */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="international">
              International Data Transfers
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain is based in the United States. Your data may be transferred to and processed in the U.S. or other countries where our service providers operate.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              For users in the European Economic Area (EEA), United Kingdom, or Switzerland: We rely on Standard Contractual Clauses approved by the European Commission for international transfers. By using BaoBrain, you consent to the transfer of your information to the U.S. and other jurisdictions.
            </Text>

            {/* CHANGES */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="changes">
              Changes to This Privacy Policy
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              When we make changes:
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>We will update the "Last Updated" date at the top</PolarisList.Item>
              <PolarisList.Item>We will post the revised policy on this page</PolarisList.Item>
              <PolarisList.Item>For material changes, we will notify you via email or prominent notice in the app</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              Your continued use of BaoBrain after changes are posted constitutes acceptance of the updated Privacy Policy.
            </Text>

            {/* CONTACT */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="contact">
              Contact Us
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices:
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="4">
              <Text as="span" fontWeight="semibold">Email:</Text>
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>General inquiries: <Link url="mailto:privacy@baobrain.com">privacy@baobrain.com</Link></PolarisList.Item>
              <PolarisList.Item>Security issues: <Link url="mailto:security@baobrain.com">security@baobrain.com</Link></PolarisList.Item>
              <PolarisList.Item>Support: <Link url="mailto:support@baobrain.com">support@baobrain.com</Link></PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="4">
              <Text as="span" fontWeight="semibold">Mail:</Text>
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain LLC
              <br />
              4539 N 22ND ST
              <br />
              STE R
              <br />
              PHOENIX, AZ, 85016, USA
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="4">
              We will respond to all requests within 30 days.
            </Text>
          </TextContainer>
        </Layout.Section>
      </Layout>
    </Page>
  );
}